import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';

import { api } from 'lib/request/api';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import type { SavedWellFilter } from 'types/type-curve-workspace-show';
import type { ColorByConfig } from '../types';
import {
  tcSavedWellFiltersAtom,
  tcColorByRulesAtom,
  tcFiltersLocalOnlyAtom,
  tcInventoryAreaFormationsAtom,
  tcInventoryGunBarrelScopeAtom,
} from './atoms';
import {
  SAVED_WELL_FILTERS_KEY,
  useSavedWellFiltersQuery,
} from './use-saved-well-filters';
import { camelizeDeep } from './saved-well-filter-definition';
import {
  colorByConfigToDefinition,
  definitionToColorByConfig,
} from './saved-color-by-definition';
import { DEFAULT_WELL_QUALIFIER_RULE } from './utils';

// Color-by rules are persisted as analog_well_filter rows (kind='color') on the
// SAME endpoint as wells filters, so they share the query cache key.
const filtersPath = (workspaceId: number) =>
  `type_curve_workspaces/${workspaceId}/header/filters`;
const filterItemPath = (workspaceId: number, id: number) =>
  `${filtersPath(workspaceId)}/${id}`;
const filtersKey = (workspaceId: number | null): QueryKey => [
  SAVED_WELL_FILTERS_KEY,
  workspaceId,
];

function useInvalidateFilters() {
  const queryClient = useQueryClient();
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  return useCallback(
    () =>
      void queryClient.invalidateQueries({
        queryKey: filtersKey(workspaceId),
      }),
    [queryClient, workspaceId]
  );
}

// Persisted color-by rules (kind='color') derived from the shared saved-filters
// mirror atom.
export function useSavedColorByRules(): SavedWellFilter[] {
  const saved = useAtomValue(tcSavedWellFiltersAtom);
  return useMemo(() => saved.filter((r) => r.kind === 'color'), [saved]);
}

// ─── CRUD mutations (kind='color'), mirroring the cross-plot hooks ───

function useCreateColorBy() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { name: string; disabled: boolean; definition: Record<string, unknown> }
  >({
    mutationFn: ({ name, disabled, definition }) =>
      api
        .post(filtersPath(workspaceId as number), {
          json: { name, disabled, kind: 'color', definition },
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

function useUpdateColorBy() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { id: number; disabled?: boolean; definition: Record<string, unknown> }
  >({
    mutationFn: ({ id, disabled, definition }) =>
      api
        .patch(filterItemPath(workspaceId as number, id), {
          json: { disabled, definition },
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

function useDeleteColorBy() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<unknown, unknown, { id: number }>({
    mutationFn: ({ id }) =>
      api.delete(filterItemPath(workspaceId as number, id)),
    onSettled: invalidate,
  });
}

// IDs of default rules that are auto-injected and should never be persisted.
const NON_PERSISTED_RULE_IDS = new Set([DEFAULT_WELL_QUALIFIER_RULE.ruleId]);

// Reconcile client color-by rules against persisted rows: POST new rules (no
// matching row by name), PATCH changed definitions/disabled, DELETE rows whose
// rule was removed. Keyed by `name` = the client ruleId.
function useColorBySync() {
  const localOnly = useAtomValue(tcFiltersLocalOnlyAtom);
  const saved = useSavedColorByRules();
  const create = useCreateColorBy();
  const update = useUpdateColorBy();
  const del = useDeleteColorBy();

  return useCallback(
    async (rules: ColorByConfig[]) => {
      // View-only page: a published workspace, or the Inventory Workspace.
      if (localOnly) return;
      const persistable = rules.filter(
        (r) => !NON_PERSISTED_RULE_IDS.has(r.ruleId)
      );
      const ops: Promise<unknown>[] = [];
      const byName = new Map(saved.map((r) => [r.name, r]));
      const ruleIds = new Set(persistable.map((r) => r.ruleId));

      for (const rule of persistable) {
        const def = colorByConfigToDefinition(rule);
        const disabled = !rule.enabled;
        const row = byName.get(rule.ruleId);
        if (!row) {
          ops.push(
            create.mutateAsync({ name: rule.ruleId, disabled, definition: def })
          );
        } else {
          const savedDef = camelizeDeep(row.definition ?? {});
          if (
            JSON.stringify(savedDef) !== JSON.stringify(def) ||
            row.disabled !== disabled
          ) {
            ops.push(
              update.mutateAsync({ id: row.id, disabled, definition: def })
            );
          }
        }
      }
      for (const row of saved) {
        if (!ruleIds.has(row.name)) {
          ops.push(del.mutateAsync({ id: row.id }));
        }
      }
      await Promise.all(ops);
    },
    [localOnly, saved, create, update, del]
  );
}

// Page-load hydration + change persistence for color-by rules. Mount once (next
// to useCrossPlotsInit). Hydrates tcColorByRulesAtom from persisted rows, then
// debounced-reconciles subsequent client changes back to the backend.
export function useColorByInit() {
  const query = useSavedWellFiltersQuery();
  const setRules = useSetAtom(tcColorByRulesAtom);
  const rules = useAtomValue(tcColorByRulesAtom);
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const sync = useColorBySync();
  const initedRef = useRef(false);

  const { data, isSuccess } = query;

  // Hydrate once from persisted color-by rows. The default well qualifier rule
  // is never persisted, so it is carried over from whatever is already in the
  // atom rather than prepended unconditionally — usePromotedContextDefaults owns
  // whether it should exist at all (a blank/unpromoted workspace has no colour
  // rule until its Type Curve is promoted). Preserving it here instead of
  // re-adding it makes the two hooks order-independent: whichever runs second
  // sees the other's decision.
  useEffect(() => {
    if (initedRef.current || !isSuccess || !data) return;
    initedRef.current = true;
    const colorRows = data.filter((r) => r.kind === 'color');
    if (colorRows.length > 0) {
      const hydrated = colorRows.map((r) =>
        definitionToColorByConfig(r.name, r.disabled, r.definition)
      );
      setRules((prev) => {
        return prev.some((r) => r.ruleId === DEFAULT_WELL_QUALIFIER_RULE.ruleId)
          ? [DEFAULT_WELL_QUALIFIER_RULE, ...hydrated]
          : hydrated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  // Inventory Workspace: colour wells by Formation by default. Once the area's
  // formations are loaded, if there's no formation rule yet, seed one from the
  // selected type curve area's formations and their backend colours. The
  // saved-filter query is disabled off a promoted Type Curve workspace, so the
  // Inventory Workspace has no persisted color-by rows to hydrate/race with; the
  // `.some(formation)` guard still prevents a duplicate if one ever arrives.
  // Runs once per mount; a user delete stays gone for the session. Gated on
  // inventory scope, so plain Type Curve pages are unaffected.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const areaFormations = useAtomValue(tcInventoryAreaFormationsAtom);
  const seededFormationRef = useRef(false);
  useEffect(() => {
    if (seededFormationRef.current) return;
    if (inventoryScope === null) return;
    if (!areaFormations || areaFormations.length === 0) return;
    seededFormationRef.current = true;
    setRules((prev) => {
      if (prev.some((r) => r.attribute === 'formation')) return prev;
      const rule: ColorByConfig = {
        ruleId: 'inventory-default-formation',
        attribute: 'formation',
        selectedValues: areaFormations.map((f) => f.formation),
        valueColors: Object.fromEntries(
          areaFormations.map((f) => [f.formation, f.color])
        ),
        applyTo: {
          map: true,
          probitPlot: false,
          monthlyVolumes: false,
          ratios: false,
          crossPlot: true,
        },
        enabled: true,
      };
      return [...prev, rule];
    });
  }, [inventoryScope, areaFormations, setRules]);

  // Persist subsequent client changes (debounced reconcile against saved rows).
  // Use a ref for `sync` so the debounced function is stable — without this,
  // query invalidation after a PATCH causes `saved` → `sync` → `debouncedSync`
  // to change references, which re-triggers the effect and creates an infinite
  // PATCH loop (sync → PATCH → invalidate → saved changes → sync changes →
  // debouncedSync changes → effect fires → sync again → ...).
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const debouncedSync = useMemo(
    () => debounce((next: ColorByConfig[]) => void syncRef.current(next), 600),
    []
  );
  useEffect(() => {
    if (!initedRef.current) return;
    debouncedSync(rules);
  }, [rules, debouncedSync]);
  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  return query;
}
